/**
 * ScoreTrajectoryChart — handoff §3.5.
 *
 * Four series (Quality, IA, PI, Total) over snapshot dates, plus a
 * horizontal reference line at the goal total (default 75). IA and PI
 * values can be null on snapshots prior to first attestation — that's
 * normal; Recharts gaps the line where the value is null.
 *
 * Tensaw fallback note: `@tensaw/visualization/charts/LineChart` doesn't
 * expose a `ReferenceLine` or escape-hatch for custom Recharts children,
 * which the goal-line requires. Per handoff §10.1 fallback policy, this
 * uses Recharts directly. Flag for v1.4: add `referenceLines?: { y; label }[]`
 * to `LineChartProps`.
 */
import { useMemo } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart as RechartsLineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Widget } from "@tensaw/design-system";
import type { TrajectoryPoint } from "@/api/models";
import { toKpiNumber } from "@/lib/format";
import type { PinnedContext } from "./types";

export interface ScoreTrajectoryChartProps {
  trajectory: TrajectoryPoint[];
  /** Goal total as a string (from the API) — default 75. */
  goalTotal: string | null;
  /**
   * When non-null, the chart renders a vertical dashed marker at the
   * pinned meeting's as-of time (or the closest snapshot date ≤ it).
   * The trajectory still extends through today.
   */
  pinned: PinnedContext | null;
}

interface ChartRow {
  date: string;
  quality: number | null;
  ia: number | null;
  pi: number | null;
  total: number | null;
}

const SERIES_COLORS = {
  quality: "#149A9A",
  ia: "#8B5CF6",
  pi: "#F59E0B",
  total: "#1F2937",
} as const;

/**
 * Compute the x-axis value for the pinned marker. Recharts categorical
 * x-axes match by exact value, so we find the most recent snapshot
 * date ≤ pinned_as_of and use that as the marker date.
 *
 * Exported for unit testing — the date-comparison logic is subtle
 * (parsing "YYYY-MM-DD" as UTC midnight vs the ISO `pinnedAsOf` which
 * carries its own timezone semantics).
 *
 * Returns null when:
 *   - no `pinnedAsOf` was supplied
 *   - `trajectoryDates` is empty
 *   - the `pinnedAsOf` string can't be parsed
 *   - every snapshot date is after `pinnedAsOf`
 */
export function computePinnedMarkerDate(
  trajectoryDates: string[],
  pinnedAsOf: string | null | undefined,
): string | null {
  if (!pinnedAsOf || trajectoryDates.length === 0) return null;
  const asOf = new Date(pinnedAsOf);
  if (Number.isNaN(asOf.getTime())) return null;
  // Walk newest-first; pick first snapshot whose date is on or before asOf.
  for (let i = trajectoryDates.length - 1; i >= 0; i--) {
    const d = new Date(`${trajectoryDates[i]!}T00:00:00Z`);
    if (!Number.isNaN(d.getTime()) && d.getTime() <= asOf.getTime()) {
      return trajectoryDates[i]!;
    }
  }
  return null;
}

export function ScoreTrajectoryChart({ trajectory, goalTotal, pinned }: ScoreTrajectoryChartProps) {
  const data = useMemo<ChartRow[]>(
    () =>
      trajectory.map((p) => ({
        date: p.snapshot_date,
        quality: toKpiNumber(p.quality),
        ia: toKpiNumber(p.ia),
        pi: toKpiNumber(p.pi),
        total: toKpiNumber(p.total),
      })),
    [trajectory],
  );

  const goal = toKpiNumber(goalTotal) ?? 75;

  const pinnedMarkerDate = useMemo<string | null>(
    () => computePinnedMarkerDate(data.map((d) => d.date), pinned?.pinnedAsOf),
    [data, pinned],
  );

  return (
    <Widget title="Score trajectory" subtitle={`Goal ${goal} · weekly snapshot`}>
      {data.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500">
          No snapshots yet — run a recompute to populate the chart.
        </div>
      ) : (
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <RechartsLineChart data={data} margin={{ top: 16, right: 24, bottom: 8, left: 12 }}>
              <CartesianGrid stroke="#E5E7EB" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fill: "#6B7280", fontSize: 12 }}
                tickLine={false}
                axisLine={{ stroke: "#E5E7EB" }}
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fill: "#6B7280", fontSize: 12 }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={{
                  border: "1px solid #E5E7EB",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelStyle={{ fontWeight: 600, color: "#1F2937" }}
              />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} iconType="line" />
              <ReferenceLine
                y={goal}
                stroke="#149A9A"
                strokeDasharray="4 4"
                label={{ value: `Goal ${goal}`, position: "insideTopRight", fill: "#149A9A", fontSize: 11 }}
              />
              {pinned && pinnedMarkerDate ? (
                <ReferenceLine
                  x={pinnedMarkerDate}
                  stroke="#6B7280"
                  strokeDasharray="2 4"
                  label={{
                    value: pinned.meetingLabel,
                    position: "top",
                    fill: "#6B7280",
                    fontSize: 11,
                  }}
                />
              ) : null}
              <Line
                type="linear"
                dataKey="quality"
                name="Quality"
                stroke={SERIES_COLORS.quality}
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls={false}
              />
              <Line
                type="linear"
                dataKey="ia"
                name="Improvement Activities"
                stroke={SERIES_COLORS.ia}
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls={false}
              />
              <Line
                type="linear"
                dataKey="pi"
                name="Promoting Interop."
                stroke={SERIES_COLORS.pi}
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls={false}
              />
              <Line
                type="linear"
                dataKey="total"
                name="Total"
                stroke={SERIES_COLORS.total}
                strokeWidth={2.5}
                dot={{ r: 4 }}
                connectNulls={false}
              />
            </RechartsLineChart>
          </ResponsiveContainer>
        </div>
      )}
    </Widget>
  );
}
